/*
 *
 *
 *
 *   Copyright (c) 2022. TinyMedic Pvt Ltd  - All Rights Reserved.
 *   This file is part of the API module  of the project: TinyMedic-Platform.
 *
 *
 *   Proprietary and confidential.
 *
 *   Unauthorized copying of this file, via any medium is strictly prohibited.
 *    Any resources of   TinyMedic-Platform can not be copied and/or distributed without the express permission of TinyMedic Pvt Ltd.
 *
 *
 *
 */

import React, { useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button, Col, FormText, Row, UncontrolledTooltip } from 'reactstrap';
import { Translate, ValidatedField, ValidatedForm, isNumber, translate } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities as getPatientProcedures } from 'app/entities/patient-procedure/patient-procedure.reducer';
import { getEntities as getLabPrescriptions } from 'app/entities/lab-prescription/lab-prescription.reducer';
import { getEntities as getRadiologyStudies } from 'app/entities/radiology-study/radiology-study.reducer';
import { MemberRole } from 'app/shared/model/enumerations/member-role.model';
import { LabServiceType } from 'app/shared/model/enumerations/lab-service-type.model';
import { createEntity, getEntity, reset, updateEntity } from './intra-op-investigation.reducer';

export const IntraOPInvestigationUpdate = () => {
  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const { id } = useParams<'id'>();
  const isNew = id === undefined;

  const patientProcedures = useAppSelector(state => state.patientProcedure.entities);
  const labPrescriptions = useAppSelector(state => state.labPrescription.entities);
  const radiologyStudies = useAppSelector(state => state.radiologyStudy.entities);
  const intraOPInvestigationEntity = useAppSelector(state => state.intraOPInvestigation.entity);
  const loading = useAppSelector(state => state.intraOPInvestigation.loading);
  const updating = useAppSelector(state => state.intraOPInvestigation.updating);
  const updateSuccess = useAppSelector(state => state.intraOPInvestigation.updateSuccess);
  const memberRoleValues = Object.keys(MemberRole);
  const labServiceTypeValues = Object.keys(LabServiceType);

  const handleClose = () => {
    navigate(`/intra-op-investigation${location.search}`);
  };

  useEffect(() => {
    if (isNew) {
      dispatch(reset());
    } else {
      dispatch(getEntity(id));
    }

    dispatch(getPatientProcedures({}));
    dispatch(getLabPrescriptions({}));
    dispatch(getRadiologyStudies({}));
  }, []);

  useEffect(() => {
    if (updateSuccess) {
      handleClose();
    }
  }, [updateSuccess]);

  const saveEntity = values => {
    if (values.cursor !== undefined && typeof values.cursor !== 'number') {
      values.cursor = Number(values.cursor);
    }
    if (values.lastUpdated !== undefined && typeof values.lastUpdated !== 'number') {
      values.lastUpdated = Number(values.lastUpdated);
    }
    if (values.microSec !== undefined && typeof values.microSec !== 'number') {
      values.microSec = Number(values.microSec);
    }
    if (values.timestampEntry !== undefined && typeof values.timestampEntry !== 'number') {
      values.timestampEntry = Number(values.timestampEntry);
    }

    const entity = {
      ...intraOPInvestigationEntity,
      ...values,
      surgery: patientProcedures.find(it => it.id.toString() === values.surgery?.toString()),
      labPrescription: labPrescriptions.find(it => it.id.toString() === values.labPrescription?.toString()),
      radiologyStudy: radiologyStudies.find(it => it.id.toString() === values.radiologyStudy?.toString()),
    };

    if (isNew) {
      dispatch(createEntity(entity));
    } else {
      dispatch(updateEntity(entity));
    }
  };

  const defaultValues = () =>
    isNew
      ? {}
      : {
          orderedBy: 'DOCTOR',
          serviceType: 'RADIOLOGY',
          ...intraOPInvestigationEntity,
          surgery: intraOPInvestigationEntity?.surgery?.id,
          labPrescription: intraOPInvestigationEntity?.labPrescription?.id,
          radiologyStudy: intraOPInvestigationEntity?.radiologyStudy?.id,
        };

  return (
    <div>
      <Row className="justify-content-center">
        <Col md="8">
          <h2 id="tinyMedicRestApp.intraOPInvestigation.home.createOrEditLabel" data-cy="IntraOPInvestigationCreateUpdateHeading">
            <Translate contentKey="tinyMedicRestApp.intraOPInvestigation.home.createOrEditLabel">
              Create or edit a IntraOPInvestigation
            </Translate>
          </h2>
        </Col>
      </Row>
      <Row className="justify-content-center">
        <Col md="8">
          {loading ? (
            <p>Loading...</p>
          ) : (
            <ValidatedForm defaultValues={defaultValues()} onSubmit={saveEntity}>
              {!isNew ? (
                <ValidatedField
                  name="id"
                  required
                  readOnly
                  id="intra-op-investigation-id"
                  label={translate('tinyMedicRestApp.intraOPInvestigation.id')}
                  validate={{ required: true }}
                />
              ) : null}
              <ValidatedField
                label={translate('tinyMedicRestApp.intraOPInvestigation.orderedBy')}
                id="intra-op-investigation-orderedBy"
                name="orderedBy"
                data-cy="orderedBy"
                type="select"
              >
                {memberRoleValues.map(memberRole => (
                  <option value={memberRole} key={memberRole}>
                    {translate(`tinyMedicRestApp.MemberRole.${memberRole}`)}
                  </option>
                ))}
              </ValidatedField>
              <UncontrolledTooltip target="orderedByLabel">
                <Translate contentKey="tinyMedicRestApp.intraOPInvestigation.help.orderedBy" />
              </UncontrolledTooltip>
              <ValidatedField
                label={translate('tinyMedicRestApp.intraOPInvestigation.orderedById')}
                id="intra-op-investigation-orderedById"
                name="orderedById"
                data-cy="orderedById"
                type="text"
              />
              <UncontrolledTooltip target="orderedByIdLabel">
                <Translate contentKey="tinyMedicRestApp.intraOPInvestigation.help.orderedById" />
              </UncontrolledTooltip>
              <ValidatedField
                label={translate('tinyMedicRestApp.intraOPInvestigation.serviceType')}
                id="intra-op-investigation-serviceType"
                name="serviceType"
                data-cy="serviceType"
                type="select"
              >
                {labServiceTypeValues.map(labServiceType => (
                  <option value={labServiceType} key={labServiceType}>
                    {translate(`tinyMedicRestApp.LabServiceType.${labServiceType}`)}
                  </option>
                ))}
              </ValidatedField>
              <ValidatedField
                label={translate('tinyMedicRestApp.intraOPInvestigation.active')}
                id="intra-op-investigation-active"
                name="active"
                data-cy="active"
                check
                type="checkbox"
              />
              <UncontrolledTooltip target="activeLabel">
                <Translate contentKey="tinyMedicRestApp.intraOPInvestigation.help.active" />
              </UncontrolledTooltip>
              <ValidatedField
                label={translate('tinyMedicRestApp.intraOPInvestigation.cursor')}
                id="intra-op-investigation-cursor"
                name="cursor"
                data-cy="cursor"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <UncontrolledTooltip target="cursorLabel">
                <Translate contentKey="tinyMedicRestApp.intraOPInvestigation.help.cursor" />
              </UncontrolledTooltip>
              <ValidatedField
                label={translate('tinyMedicRestApp.intraOPInvestigation.lastUpdated')}
                id="intra-op-investigation-lastUpdated"
                name="lastUpdated"
                data-cy="lastUpdated"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <UncontrolledTooltip target="lastUpdatedLabel">
                <Translate contentKey="tinyMedicRestApp.intraOPInvestigation.help.lastUpdated" />
              </UncontrolledTooltip>
              <ValidatedField
                label={translate('tinyMedicRestApp.intraOPInvestigation.microSec')}
                id="intra-op-investigation-microSec"
                name="microSec"
                data-cy="microSec"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <UncontrolledTooltip target="microSecLabel">
                <Translate contentKey="tinyMedicRestApp.intraOPInvestigation.help.microSec" />
              </UncontrolledTooltip>
              <ValidatedField
                label={translate('tinyMedicRestApp.intraOPInvestigation.timestampEntry')}
                id="intra-op-investigation-timestampEntry"
                name="timestampEntry"
                data-cy="timestampEntry"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <UncontrolledTooltip target="timestampEntryLabel">
                <Translate contentKey="tinyMedicRestApp.intraOPInvestigation.help.timestampEntry" />
              </UncontrolledTooltip>
              <ValidatedField
                id="intra-op-investigation-surgery"
                name="surgery"
                data-cy="surgery"
                label={translate('tinyMedicRestApp.intraOPInvestigation.surgery')}
                type="select"
                required
              >
                <option value="" key="0" />
                {patientProcedures
                  ? patientProcedures.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <FormText>
                <Translate contentKey="entity.validation.required">This field is required.</Translate>
              </FormText>
              <ValidatedField
                id="intra-op-investigation-labPrescription"
                name="labPrescription"
                data-cy="labPrescription"
                label={translate('tinyMedicRestApp.intraOPInvestigation.labPrescription')}
                type="select"
              >
                <option value="" key="0" />
                {labPrescriptions
                  ? labPrescriptions.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <ValidatedField
                id="intra-op-investigation-radiologyStudy"
                name="radiologyStudy"
                data-cy="radiologyStudy"
                label={translate('tinyMedicRestApp.intraOPInvestigation.radiologyStudy')}
                type="select"
              >
                <option value="" key="0" />
                {radiologyStudies
                  ? radiologyStudies.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <Button tag={Link} id="cancel-save" data-cy="entityCreateCancelButton" to="/intra-op-investigation" replace color="info">
                <FontAwesomeIcon icon="arrow-left" />
                &nbsp;
                <span className="d-none d-md-inline">
                  <Translate contentKey="entity.action.back">Back</Translate>
                </span>
              </Button>
              &nbsp;
              <Button color="primary" id="save-entity" data-cy="entityCreateSaveButton" type="submit" disabled={updating}>
                <FontAwesomeIcon icon="save" />
                &nbsp;
                <Translate contentKey="entity.action.save">Save</Translate>
              </Button>
            </ValidatedForm>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default IntraOPInvestigationUpdate;
